import { Action, Dispatch, Middleware } from '@reduxjs/toolkit';
import { randomUUID } from '@ag-ui/client';
import type {
  AgentSubscriber,
  HttpAgent,
  Message,
  StateSnapshotEvent,
  TextMessageContentEvent,
  ToolCallEndEvent,
  ToolCallResultEvent,
  ToolCallStartEvent,
} from '@ag-ui/client';
import { AlertType } from '../../../../libs/models/AlertType';
import { showAlertToast } from '../../app/appSlice';
import {
  AppDispatch,
  RootState,
  StoreMiddlewareAPI,
  getSelectedChatID_V2,
} from '../../../app/store';
import { AIModelName } from '../../../../libs/models/app/AIModel';
import { IFileAnalyzedCacheResult } from '../../../../libs/models/conversations/File';
import { WebSearchSource } from '../../../../libs/models/app/Plugin';
import { IMCPTool } from '../../../../libs/models/app/mcp/Tool';
import { ChatMessage, ChatRole, TextContent } from '../../../../libs/models/conversations/ChatMessage';
import { ChatService_v2 } from '../../../../libs/services/ChatService_v2';
import { ForwardedProps, FunctionResultData } from '../../../../libs/models/agui/AgUiEvent';
import { abortAgent, getLastRunParams, getOrCreateAgent, setLastRunParams, toBackendUrl } from './agUiClient';
import {
  createFunctionCallUpdate,
  createFunctionResultUpdate,
  createTextStreamUpdate,
  handleCustomSnapshot,
  resolveAssistantSlotId,
  toAgUiTools,
  skillTools,
} from './agUiHelpers';


const USE_TEST_ENDPOINT = false;
const TEST_ENDPOINT = '/api/chatagent/test';

/** 도구 결과 run: 켜져 있으면 url을 /test로 바꿔 호출하고 복구 */
async function runToolResultAgent(
  agent: HttpAgent,
  runParams: Parameters<HttpAgent['runAgent']>[0],
  subscriber: AgentSubscriber,
): Promise<void> {
  if (!USE_TEST_ENDPOINT) {
    await agent.runAgent(runParams, subscriber);
    return;
  }
  const originalUrl = agent.url;
  agent.url = toBackendUrl(TEST_ENDPOINT);
  try {
    await agent.runAgent(runParams, subscriber);
  } finally {
    agent.url = originalUrl;
  }
}

/** 도구 결과 전송 시 agent.messages 구성*/
function buildToolResultMessages(agent: HttpAgent, toolMessages: Message | Message[]): Message[] {
  // BE 요청: assistant 도구호출 메시지의 id 를 toolCall id 와 동일하게 강제
  const fixed = agent.messages.map((m) => {
    const tc = (m as { toolCalls?: Array<{ id?: string }> }).toolCalls;
    if (m.role === 'assistant' && tc?.length && tc[0].id) {
      return { ...m, id: tc[0].id };
    }
    return m;
  });
  return [...fixed, ...(Array.isArray(toolMessages) ? toolMessages : [toolMessages])];
}

// ============================================================
// 1. 타입
// ============================================================

interface AgUiAction extends Action {
  payload: {
    message?: string;
    sessionId?: string;
    agentId?: string;
    model: AIModelName;
    attachFiles?: IFileAnalyzedCacheResult[];
    cacheMode?: boolean;
    isDraft?: boolean;
    webSearchOn: boolean;
    webSearchSource: WebSearchSource;
    mcpTools?: IMCPTool[];
    agentMode?: boolean;
    /** ABC Lab 변수 등 추가 필드 */
    additionalProperties?: Record<string, unknown>;
    /** functionResult 회신용 */
    callId?: string;
    result?: Record<string, unknown>;
    /** HITL "이 세션 항상 승인" 시 세션 승인 모드 (Options 로 전송) */
    sessionApprovalMode?: string;
  };
}

interface RunOptions {
  forwardedProps: ForwardedProps;
  tools: ReturnType<typeof toAgUiTools>;
  newUserMessage?: { content: string };
  newToolResultMessages?: Array<{ toolCallId: string; content: string }>;
}

/** START 이벤트에만 있는 정보(parentMessageId/name)를 END·RESULT로 전달 */
interface PendingToolCallMeta {
  toolCallName: string;
  parentMessageId?: string;
}

/** SDK 이벤트 핸들러가 공유하는 컨텍스트 */
interface SubscriberCtx {
  store: StoreMiddlewareAPI;
  sessionId: string;
}

// ============================================================
// 2. 세션 상태 (sessionId별)
// ============================================================

const session = {
  /** TOOL_CALL_START~END 사이 메타 누적 (toolCallId 기준, 전 세션 공용) */
  toolCalls: new Map<string, PendingToolCallMeta>(),

  /** 클라이언트가 직접 실행하는 MCP 도구 이름 (sessionId별). 여기 없으면 HITL 등으로 보고 실행 안 함 */
  mcpToolNames: new Map<string, Set<string>>(),

  /** 로컬 도구 실행 후 결과 회신으로 곧 다음 run 이 이어지는 sessionId.  */
  pendingLocalRun: new Set<string>(),

  /** 이번 스트림에서 호출된 클라이언트 도구 대기열 (sessionId별). RUN_FINISHED 후 실행한다. */
  pendingToolCalls: new Map<string, Array<{ toolCallId: string; name: string; args: Record<string, unknown> }>>(),

  /** 이번 스트림에서 서버가 이미 실행한 도구(TOOL_CALL_RESULT 수신) toolCallId. 클라이언트는 재실행하지 않는다. */
  serverExecuted: new Map<string, Set<string>>(),

  clear() {
    this.toolCalls.clear();
  },
};

// ============================================================
// 3. 유틸
// ============================================================

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

function setBotStatus(store: StoreMiddlewareAPI, sessionId: string, isActive: boolean): void {
  store.dispatch({
    type: 'conversations/v2/updateBotResponseStatus',
    payload: { chatRoomId: sessionId, isActive },
  });
}

/** 스트리밍 메시지(텍스트/도구카드/결과) 1건을 v2 슬라이스로 dispatch */
function dispatchStreamingMessage(ctx: SubscriberCtx, message: unknown): void {
  ctx.store.dispatch({
    type: 'conversations/v2/addStreamingMessageToConversationFromServer',
    payload: { chatRoomId: ctx.sessionId, message },
  });
}

function getConversationMessages(store: StoreMiddlewareAPI, sessionId: string): ChatMessage[] {
  return store.getState().conversations_v2.conversations[sessionId]?.messages?.data ?? [];
}

/** 이전 메시지의 requestMeta로부터 forwardedProps 복원 (retry / HITL 응답 시) */
function buildForwardedPropsFromMessage(message: ChatMessage | undefined, model: AIModelName): ForwardedProps {
  const meta = message?.requestMeta as
    | { agentId?: string; agentMode?: boolean; webSearchOn?: boolean; webSearchSource?: WebSearchSource }
    | undefined;
  return {
    model,
    agentId: meta?.agentId,
    agentMode: meta?.agentMode ?? false,
    webSearchOn: meta?.webSearchOn ?? false,
    webSearchSource: meta?.webSearchSource,
    attachFiles: [],
  };
}

/** Redux 메시지 배열에서 agentId가 있는 컨텍스트 메시지 찾기 */
function findAgentContextMessage(msgs: ChatMessage[], preferred: ChatMessage | undefined): ChatMessage | undefined {
  if ((preferred?.requestMeta as { agentId?: string } | undefined)?.agentId) return preferred;
  return [...msgs]
    .reverse()
    .find((m) => m.role === ChatRole.Assistant && (m.requestMeta as { agentId?: string } | undefined)?.agentId);
}

// ============================================================
// 4. SDK Subscriber — AG-UI 이벤트 핸들러
// ============================================================

function handleTextDelta(ctx: SubscriberCtx, event: TextMessageContentEvent): void {
  const text = typeof event.delta === 'string' ? event.delta : '';
  if (!text || !event.messageId) return;

  const resolvedId = resolveAssistantSlotId(() => ctx.store.getState(), ctx.sessionId, event.messageId);
  if (!resolvedId) return;
  dispatchStreamingMessage(ctx, createTextStreamUpdate(ctx.sessionId, resolvedId, text));
}

function handleSnapshot(ctx: SubscriberCtx, event: StateSnapshotEvent): void {
  const snapshot = event.snapshot as { snapshotType?: string } & Record<string, unknown>;
  if (snapshot?.snapshotType !== 'chatmessage' && snapshot?.snapshotType !== 'agentstate-update') return;
  handleCustomSnapshot(ctx.store.dispatch as AppDispatch, ctx.sessionId, snapshot as Parameters<typeof handleCustomSnapshot>[2]);
}

/** TOOL_CALL_START — p */
function handleToolCallStart(event: ToolCallStartEvent): void {
  if (!event.toolCallId) return;
  session.toolCalls.set(event.toolCallId, {
    toolCallName: event.toolCallName ?? '',
    parentMessageId: event.parentMessageId,
  });
}

/** TOOL_CALL_END — 도구 카드 표시 + (클라이언트 실행 대상이면) 대기열에 저장.
 *  실제 실행은 SSE 스트림(RUN_FINISHED) 종료 후에 한다. */
function handleToolCallEnd(
  ctx: SubscriberCtx,
  event: ToolCallEndEvent,
  toolCallName: string,
  toolCallArgs: Record<string, unknown>,
): void {
  const meta = session.toolCalls.get(event.toolCallId);
  const name = toolCallName !== '' ? toolCallName : (meta?.toolCallName ?? '');

  showToolCallCard(ctx, event.toolCallId, name, toolCallArgs, meta);

  // 로컬에서 처리할 도구(MCP 도구 or 스킬 도구)면 대기열에 담아두고, 스트림 종료 후 실행한다.
  if (!shouldRunLocally(ctx.sessionId, name)) return;
  const list = session.pendingToolCalls.get(ctx.sessionId) ?? [];
  list.push({ toolCallId: event.toolCallId, name, args: toolCallArgs });
  session.pendingToolCalls.set(ctx.sessionId, list);
}

/** 도구 호출 카드(functionCall)를 화면에 표시 */
function showToolCallCard(
  ctx: SubscriberCtx,
  toolCallId: string,
  name: string,
  toolCallArgs: Record<string, unknown>,
  meta: PendingToolCallMeta | undefined,
): void {
  const parent = resolveAssistantSlotId(() => ctx.store.getState(), ctx.sessionId, meta?.parentMessageId);
  if (!parent || !name) return;

  dispatchStreamingMessage(ctx, createFunctionCallUpdate(ctx.sessionId, parent, toolCallId, name, JSON.stringify(toolCallArgs)));
}

/** 스킬 도구 */
function isSkillTool(name: string): boolean {
  return name === 'load_skill' || name === 'read_skill_resource' || name === 'run_skill_script';
}

// 스킬 도구 + 명령 실행 도구 = 클라이언트가 직접 실행하는 도구
function isLocalTool(name: string): boolean {
  return isSkillTool(name) || name === 'run_command';
}

/**
 * 클라이언트(데스크탑 앱)가 직접 실행할 도구인지 판단.
 * - 스킬/명령 도구 또는
 * - run 에 보낸 MCP 도구 목록에 있는 도구
 */
function shouldRunLocally(sessionId: string, name: string): boolean {
  if (!name || !window.electron?.isElectron) return false;
  if (isLocalTool(name)) return true;
  return session.mcpToolNames.get(sessionId)?.has(name) ?? false;
}

/** 로컬 도구(스킬 or MCP) 하나 실행 → 결과 반환 */
async function runLocalTool(name: string, args: Record<string, unknown>): Promise<unknown> {
  return isSkillTool(name)
    ? await runSkillTool(name, args)
    : name === 'run_command'
      ? await runCommandTool(args)
      : ((await window.electron.requestMCP({ toolName: name, arguments: args })) as unknown);
}

/** 스트림 종료(RUN_FINISHED) 후: 이번 스트림에 쌓인 클라이언트 도구들을 실행하고 결과를 회신한다.
 *  서버가 이미 실행한 도구(TOOL_CALL_RESULT 수신분)는 건너뛴다. */
async function runPendingToolsAndReply(ctx: SubscriberCtx): Promise<void> {
  const calls = session.pendingToolCalls.get(ctx.sessionId) ?? [];
  session.pendingToolCalls.delete(ctx.sessionId);
  const executed = session.serverExecuted.get(ctx.sessionId) ?? new Set<string>();
  session.serverExecuted.delete(ctx.sessionId);

  // 서버가 이미 실행한 도구는 클라이언트에서 재실행하지 않는다.
  const toRun = calls.filter((c) => !executed.has(c.toolCallId));
  if (toRun.length === 0) return;

  session.pendingLocalRun.add(ctx.sessionId);
  const results: Array<{ toolCallId: string; content: string }> = [];
  try {
    for (const c of toRun) {
      try {
        const result = await runLocalTool(c.name, c.args);
        dispatchLocalToolResult(ctx, c.toolCallId, result);
        results.push({ toolCallId: c.toolCallId, content: JSON.stringify(result) });
      } catch (err) {
        const errorPayload = { error: errorMessage(err) };
        dispatchLocalToolResult(ctx, c.toolCallId, errorPayload);
        results.push({ toolCallId: c.toolCallId, content: JSON.stringify(errorPayload) });
      }
    }
  } finally {
    session.pendingLocalRun.delete(ctx.sessionId);
  }

 
  await sendToolResults(ctx, results);
}

/** 로컬 실행 결과를 functionResult 카드로 즉시 화면에 표시 */
function dispatchLocalToolResult(ctx: SubscriberCtx, toolCallId: string, result: unknown): void {
  const meta = session.toolCalls.get(toolCallId);
  const parent = resolveAssistantSlotId(() => ctx.store.getState(), ctx.sessionId, meta?.parentMessageId);
  if (!parent) return;
  dispatchStreamingMessage(ctx, createFunctionResultUpdate(ctx.sessionId, parent, result as FunctionResultData, toolCallId));
}

/** 스킬 도구를 Electron 브릿지로 실행 */
async function runSkillTool(name: string, args: Record<string, unknown>): Promise<unknown> {
  const skillName = String(args.skillName ?? '');
  if (name === 'load_skill') {
    const res: { ok: boolean; skillMd?: string; error?: string } | undefined =
      await window.electron.skillGetMd?.(skillName);
    if (!res?.ok) throw new Error(res?.error ?? '스킬 로드 실패');
    return { name: skillName, content: res.skillMd };
  }
  if (name === 'read_skill_resource') {
    const res: { ok: boolean; path?: string; content?: string; error?: string } | undefined =
      await window.electron.skillReadResource?.(skillName, String(args.resourceName ?? ''));
    if (!res?.ok) throw new Error(res?.error ?? '파일 읽기 실패');
    return { path: res.path, content: res.content };
  }
  const scriptArgs = toScriptArgs(args.arguments);
  const res = await window.electron.skillRun?.(skillName, String(args.scriptName ?? ''), scriptArgs);
  if (!res?.ok) throw new Error(res?.error ?? '스크립트 실행 실패');
  return res.result;
}
function toScriptArgs(raw: unknown): string[] {
  if (Array.isArray(raw)) return raw.map(String);
  if (raw && typeof raw === 'object') {
    return Object.entries(raw as Record<string, unknown>).flatMap(([k, v]) => [`--${k}`, String(v)]);
  }
  return [];
}

/** run_command — 임의 명령 실행 (라이브러리 설치 등). BE 스펙 확정 시 변경 가능. */
async function runCommandTool(args: Record<string, unknown>): Promise<unknown> {
  const command = String(args.command ?? '');
  const cmdArgs = Array.isArray(args.args) ? args.args.map(String) : [];
  const res = await window.electron.runCommand?.(command, cmdArgs);
  if (!res?.ok) throw new Error(res?.error ?? '명령 실행 실패');
  return res.result;
}

/** TOOL_CALL_RESULT — 도구 결과 카드 표시 + 서버가 실행한 도구로 기록(클라이언트 재실행 방지) */
function handleToolCallResult(ctx: SubscriberCtx, event: ToolCallResultEvent): void {
  if (!event.toolCallId) return;

  // 서버에서 이미 실행된 도구이므로 클라이언트에서 재실행하지 않도록 표시
  const set = session.serverExecuted.get(ctx.sessionId) ?? new Set<string>();
  set.add(event.toolCallId);
  session.serverExecuted.set(ctx.sessionId, set);

  const meta = session.toolCalls.get(event.toolCallId);
  const parent = resolveAssistantSlotId(() => ctx.store.getState(), ctx.sessionId, event.messageId ?? meta?.parentMessageId);

  if (parent) {
    const result = parseMaybeJson(event.content);
    dispatchStreamingMessage(ctx, createFunctionResultUpdate(ctx.sessionId, parent, result as FunctionResultData, event.toolCallId));
  }
  session.toolCalls.delete(event.toolCallId);
}

function handleRunFinalized(ctx: SubscriberCtx): void {
  if (session.pendingLocalRun.has(ctx.sessionId)) return;
  // 스트림 종료 후, 이번 스트림에 쌓인 클라이언트 도구가 있으면 그때 실행하고 결과를 회신한다.
  if (session.pendingToolCalls.get(ctx.sessionId)?.length) {
    void runPendingToolsAndReply(ctx);
    return;
  }
  setBotStatus(ctx.store, ctx.sessionId, false);
}

function handleRunFailed(ctx: SubscriberCtx, error: Error): void {
  console.error('[AG-UI]', error);
  ctx.store.dispatch(showAlertToast({ altMessage: error.message, altType: AlertType.Info }));
  session.clear();
  setBotStatus(ctx.store, ctx.sessionId, false);
}

/** AG-UI SSE 이벤트 → 핸들러 매핑 */
function createSubscriber(store: StoreMiddlewareAPI, sessionId: string): AgentSubscriber {
  const ctx: SubscriberCtx = { store, sessionId };
  return {
    onTextMessageContentEvent: ({ event }) => { handleTextDelta(ctx, event); },
    onStateSnapshotEvent: ({ event }) => { handleSnapshot(ctx, event); },
    onToolCallStartEvent: ({ event }) => { handleToolCallStart(event); },
    onToolCallEndEvent: ({ event, toolCallName, toolCallArgs }) => {
      handleToolCallEnd(ctx, event, toolCallName, toolCallArgs);
    },
    onToolCallResultEvent: ({ event }) => { handleToolCallResult(ctx, event); },
    onRunFinalized: () => { handleRunFinalized(ctx); },
    onRunFailed: ({ error }) => { handleRunFailed(ctx, error); },
  };
}

/** 문자열이면 JSON 파싱 시도, 실패하거나 비문자열이면 원본 반환 */
function parseMaybeJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try { return JSON.parse(value); } catch { return value; }
}

// ============================================================
// 6. Run 실행
// ============================================================

/** 최초 사용자 메시지/도구 결과 전송 → run 시작 */
async function triggerRun(store: StoreMiddlewareAPI, sessionId: string, options: RunOptions): Promise<void> {
  const agent = await getOrCreateAgent(sessionId);

  // BE가 thread별 히스토리를 보관하므로 새 메시지만 전송
  const messages: Message[] = [];
  if (options.newUserMessage) {
    messages.push({ id: randomUUID(), role: 'user', content: options.newUserMessage.content });
  }
  for (const tr of options.newToolResultMessages ?? []) {
    // id 를 toolCallId 와 동일하게 (BE 요청)
    messages.push({ id: tr.toolCallId, role: 'tool', toolCallId: tr.toolCallId, content: tr.content });
  }

  // 도구 결과 재호출 시 동일 tools/forwardedProps 재사용, 직접 실행할 MCP 도구 이름 저장
  setLastRunParams(sessionId, { tools: options.tools, forwardedProps: options.forwardedProps });
  session.mcpToolNames.set(sessionId, new Set(options.tools.map((t) => t.name)));

  await runAgent(store, sessionId, agent, messages, { tools: options.tools, forwardedProps: options.forwardedProps });
}

/** 실행된 도구 결과들을 담은 새 AG-UI 요청을 서버로 전송 → run 재개 */
async function sendToolResults(ctx: SubscriberCtx, results: Array<{ toolCallId: string; content: string }>): Promise<void> {
  if (results.length === 0) return;

  const agent = await getOrCreateAgent(ctx.sessionId);
  // id 를 toolCallId 와 동일하게 (BE 요청)
  const toolMessages: Message[] = results.map((r) => ({
    id: r.toolCallId,
    role: 'tool',
    toolCallId: r.toolCallId,
    content: r.content,
  }));
  const messages = buildToolResultMessages(agent, toolMessages);
  await runAgent(ctx.store, ctx.sessionId, agent, messages, getLastRunParams(ctx.sessionId), true);
}

/** agent.messages 세팅 + runAgent 공통 실행 (에러/상태 처리 포함) */
async function runAgent(
  store: StoreMiddlewareAPI,
  sessionId: string,
  agent: HttpAgent,
  messages: Message[],
  runParams: Parameters<HttpAgent['runAgent']>[0],
  isToolResult = false,
): Promise<void> {
  agent.messages = messages;
  agent.setState({});
  setBotStatus(store, sessionId, true);

  const subscriber = createSubscriber(store, sessionId);
  try {
    if (isToolResult) {
      await runToolResultAgent(agent, runParams, subscriber);
    } else {
      await agent.runAgent(runParams, subscriber);
    }
  } catch (err) {
    if (err instanceof Error && err.name === 'AbortError') return;
    console.error('[AG-UI]', err);
    store.dispatch(showAlertToast({ altMessage: errorMessage(err), altType: AlertType.Info }));
    setBotStatus(store, sessionId, false);
    return;
  }

  void syncChatRoomTitle(store, sessionId);
}

/**
 * 첫 응답 후 생성되는 채팅방 제목을 보정한다.
 */
const chatService = new ChatService_v2();

async function syncChatRoomTitle(store: StoreMiddlewareAPI, sessionId: string): Promise<void> {
  const conversation = store.getState().conversations_v2.conversations[sessionId];
  if (!conversation || conversation.title) return;

  try {
    const chatRoom = await chatService.getChatRoomAsync(sessionId);
    if (!chatRoom.title) return;

    store.dispatch({
      type: 'conversations/v2/editConversationTitle',
      payload: { id: sessionId, newTitle: chatRoom.title },
    });
  } catch {
    // 제목 보정 실패는 대화 흐름에 영향을 주지 않는다.
  }
}

// ============================================================
// 7. 액션 핸들러
// ============================================================

function sendUserMessage(store: StoreMiddlewareAPI, payload: AgUiAction['payload']): void {
  const { sessionId, message, model } = payload;
  if (!sessionId || !message) return;

  const forwardedProps: ForwardedProps = {
    model,
    agentId: payload.agentId,
    agentMode: payload.agentMode,
    cacheMode: payload.cacheMode,
    isDraft: payload.isDraft,
    webSearchOn: payload.webSearchOn,
    webSearchSource: payload.webSearchSource,
    attachFiles: payload.attachFiles,
    ...(payload.additionalProperties ?? {}),
  };

  // 스킬 모드면 스킬 도구 3개를 tools 에 같이 보냄
  const hasSkills = !!window.electron?.isElectron && Array.isArray((forwardedProps as { agentSkills?: unknown }).agentSkills);
  const tools = hasSkills ? [...toAgUiTools(payload.mcpTools), ...skillTools] : toAgUiTools(payload.mcpTools);

  void triggerRun(store, sessionId, {
    forwardedProps,
    tools,
    newUserMessage: { content: message },
  });
}

function submitFunctionResult(store: StoreMiddlewareAPI, payload: AgUiAction['payload']): void {
  const { sessionId, model, callId, result, sessionApprovalMode } = payload;
  if (!sessionId || !callId || !result) return;

  const msgs = getConversationMessages(store, sessionId);
  const ownerMsg = msgs.find((m) =>
    m.contents?.some(
      (c) => (c as { $type?: string }).$type === 'functionCall' && (c as { callId?: string }).callId === callId,
    ),
  );
  const ctxMessage = findAgentContextMessage(msgs, ownerMsg);

  // 최초 질의와 동일하게 스킬 정보(agentSkills + skillTools)를 함께 전송
  const skillList = store.getState().tools.skills
    .filter((s) => s.enabled)
    .map((s) => ({ Name: s.name, Description: s.description }));
  const forwardedProps: ForwardedProps = {
    ...buildForwardedPropsFromMessage(ctxMessage, model),
    ...(sessionApprovalMode ? { sessionApprovalMode } : {}),
  };
  const hasSkills = !!window.electron?.isElectron && skillList.length > 0;
  if (hasSkills) forwardedProps.agentSkills = skillList;
  const tools = hasSkills ? [...toAgUiTools(payload.mcpTools), ...skillTools] : toAgUiTools(payload.mcpTools);

  void triggerRun(store, sessionId, {
    forwardedProps,
    tools,
    newToolResultMessages: [{ toolCallId: callId, content: JSON.stringify(result) }],
  });
}

function retryLastUserMessage(store: StoreMiddlewareAPI, payload: AgUiAction['payload']): void {
  const sessionId = getSelectedChatID_V2();
  const msgs = getConversationMessages(store, sessionId);
  const lastUser = [...msgs].reverse().find((m) => m.role === ChatRole.User);
  const userText = (lastUser?.contents?.[0] as TextContent | undefined)?.text ?? '';
  if (!userText) return;

  const ctxMessage = findAgentContextMessage(msgs, lastUser);
  const lastAssistant = [...msgs].reverse().find((m) => m.role === ChatRole.Assistant);

  // 재질의시 메세지 초기화
  if (lastAssistant?.id) {
    store.dispatch({
      type: 'conversations/v2/clearMessageContentsForRetry',
      payload: { sessionId, messageId: lastAssistant.id },
    });
  }

  const skillList = store.getState().tools.skills
    .filter((s) => s.enabled)
    .map((s) => ({ Name: s.name, Description: s.description }));
  const forwardedProps: ForwardedProps = {
    ...buildForwardedPropsFromMessage(ctxMessage, payload.model),
    RegenerateMessageId: lastAssistant?.id,
  };
  const hasSkills = !!window.electron?.isElectron && skillList.length > 0;
  if (hasSkills) forwardedProps.agentSkills = skillList;
  const tools = hasSkills ? [...toAgUiTools(payload.mcpTools), ...skillTools] : toAgUiTools(payload.mcpTools);

  void triggerRun(store, sessionId, {
    forwardedProps,
    tools,
    newUserMessage: { content: userText },
  });
}

function stopStreaming(): void {
  const sessionId = getSelectedChatID_V2();
  if (sessionId) abortAgent(sessionId);
}

// ============================================================
// 8. 미들웨어
// ============================================================

export const agUiMiddleware: Middleware<unknown, RootState, Dispatch<AgUiAction>> = (
  store: StoreMiddlewareAPI,
) => (next) => (action) => {
  const agUi = action as AgUiAction;
  const result = next(agUi);

  switch (agUi.type) {
    case 'conversations/v2/addMessageToConversationFromUser':
      sendUserMessage(store, agUi.payload);
      break;
    case 'conversations/v2/submitFunctionResult':
      submitFunctionResult(store, agUi.payload);
      break;
    case 'conversations/v2/retryMessageToConversationFromUser':
      retryLastUserMessage(store, agUi.payload);
      break;
    case 'conversations/v2/stopStreaming':
      stopStreaming();
      break;
  }

  return result;
};
